import React from 'react';
import { Link } from 'react-router-dom';
import BusinessContactForm from '../../components/admin/BusinessContactForm';
import {
  Bell, MapPin, Briefcase, MessageSquareQuote, HelpCircle, Shield,
  Merge, Activity, Terminal, Cog, ChevronRight, FileBarChart, Trash2
} from 'lucide-react';

/**
 * Settings hub — the single place to configure the platform.
 * All configuration sub-pages retain their existing routes; this hub
 * simply consolidates access so admins stop hunting for them.
 */
const SETTINGS_GROUPS = [
  {
    title: 'Business Configuration',
    description: 'Company-wide operational settings that affect every workflow.',
    items: [
      { label: 'Service Areas',       to: '/admin/service-areas',              icon: MapPin,          description: 'Portland ZIP codes and travel zones' },
      { label: 'Services',            to: '/admin/services',                   icon: Briefcase,       description: 'Service catalog, pricing, packages' },
      { label: 'Reviews',             to: '/admin/reviews',                    icon: MessageSquareQuote, description: 'Published customer testimonials' },
      { label: 'FAQs',                to: '/admin/faqs',                       icon: HelpCircle,      description: 'Public-facing FAQ entries' },
    ],
  },
  {
    title: 'Lead & Notification Settings',
    description: 'Control how new leads and alerts reach your team.',
    items: [
      { label: 'Lead Notifications',  to: '/admin/settings/notifications',     icon: Bell,            description: 'Email + in-app alerts on new website leads' },
    ],
  },
  {
    title: 'User Management',
    description: 'Team accounts, roles, and access history.',
    items: [
      { label: 'Users',               to: '/admin/users',                      icon: Shield,          description: 'All portal users (clients, cleaners, admins)' },
      { label: 'Duplicate Merge',     to: '/admin/duplicates',                 icon: Merge,           description: 'Merge duplicate customer accounts' },
      { label: 'Sign-In Activity',    to: '/admin/settings/sign-in-activity',  icon: Activity,        description: 'Recent login events across the platform' },
      { label: 'Audit Log',           to: '/admin/audit-log',                  icon: FileBarChart,    description: 'Every admin action, filterable' },
      { label: 'Recycle Bin',         to: '/admin/recycle-bin',                icon: Trash2,          description: 'Restore or purge soft-deleted customers (30-day window)' },
    ],
  },
  {
    title: 'Advanced',
    description: 'Platform-level tooling. Handle with care.',
    items: [
      { label: 'Integrations',        to: '/admin/integrations',               icon: Cog,             description: 'Claude, Resend, payments, Quo, Slack and Kite status' },
      { label: 'Data Cleanup',        to: '/admin/data-cleanup',               icon: Trash2,          description: 'One-time removal of the Feb 24 test scheduling requests (master admin)' },
      { label: 'Developer Mode',      to: '/admin/settings/developer',         icon: Terminal,        description: 'Feature flags & test utilities (coming soon)', disabled: true },
    ],
  },
];

const SettingsCard = ({ item }) => {
  const Icon = item.icon;
  const content = (
    <div
      className={`group flex items-start gap-3 p-4 rounded-xl border transition-all ${
        item.disabled
          ? 'border-zinc-200 bg-zinc-50 opacity-60 cursor-not-allowed'
          : 'border-zinc-200 bg-white hover:border-[#F56F05]/40 hover:shadow-md cursor-pointer'
      }`}
      data-testid={`settings-card-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
        item.disabled ? 'bg-zinc-100 text-zinc-400' : 'bg-[#F56F05]/10 text-[#F56F05] group-hover:bg-[#F56F05] group-hover:text-white transition-colors'
      }`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-semibold text-sm text-zinc-900">{item.label}</p>
          {item.disabled && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">SOON</span>}
        </div>
        <p className="text-xs text-zinc-500 mt-0.5">{item.description}</p>
      </div>
      {!item.disabled && (
        <ChevronRight className="w-4 h-4 text-zinc-300 group-hover:text-[#F56F05] transition-colors flex-shrink-0 mt-1" />
      )}
    </div>
  );
  if (item.disabled) return content;
  return <Link to={item.to} className="block">{content}</Link>;
};

export default function AdminSettingsPage() {
  return (
    <div className="min-h-screen" data-testid="admin-settings-page">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-10">
        <BusinessContactForm />
        <div className="pt-2"><h2 className="text-[10px] tracking-[0.16em] font-bold text-[#7a7c6d]">MORE SETTINGS</h2><p className="text-xs text-[#77776e] mt-1">Every existing configuration page, with its own access rules.</p></div>
        {SETTINGS_GROUPS.map(group => (
          <section key={group.title} data-testid={`settings-group-${group.title.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="mb-4">
              <h2 className="text-sm font-bold text-zinc-900 uppercase tracking-wider">{group.title}</h2>
              <p className="text-xs text-zinc-500 mt-1">{group.description}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {group.items.map(item => <SettingsCard key={item.label} item={item} />)}
            </div>
          </section>
        ))}
      </main>
    </div>
  );
}
